import React, { useState } from "react";
import "./external-templates-page.scss";
import "../../components/TemplateTable.css";
import { useTemplates, useTemplateForm } from "../../hooks";
import { TemplateTable, TemplateViewModal, TemplateFormModal, Pagination } from "../../components";

const ExternalTemplatesPage = () => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState(null);

  // Custom hooks
  const {
    templates,
    loading,
    currentPage,
    totalPages,
    handlePageChange,
    createTemplate,
    updateTemplate,
    updateTemplateInList
  } = useTemplates();

  const {
    templateForm,
    validationErrors,
    formSubmitting,
    setFormSubmitting,
    validateForm,
    handleFormChange,
    handleVariableChange,
    addVariable,
    removeVariable,
    resetForm,
    loadTemplateData,
    getFormData
  } = useTemplateForm();

  // Modal handlers
  const handleViewTemplate = (template) => {
    setSelectedTemplate(template);
    setIsViewModalOpen(true);
  };

  const handleOpenCreateModal = () => {
    resetForm();
    setIsCreateModalOpen(true);
  };

  const handleOpenEditModal = (template) => {
    loadTemplateData(template);
    setEditingTemplateId(template._id);
    setIsEditModalOpen(true);
  };

  const handleCloseModals = () => {
    setIsViewModalOpen(false);
    setIsCreateModalOpen(false);
    setIsEditModalOpen(false);
    setSelectedTemplate(null);
    setEditingTemplateId(null);
  };

  // Form submission handlers
  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setFormSubmitting(true);
    const result = await createTemplate(getFormData());
    
    if (result.success) {
      handleCloseModals();
    }
    
    setFormSubmitting(false);
  };

  const handleUpdateTemplate = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setFormSubmitting(true);
    const result = await updateTemplate(editingTemplateId, getFormData());
    
    if (result.success) {
      handleCloseModals();
    }
    
    setFormSubmitting(false);
  };

  const handleDeleteTemplate = (template) => {
    // TODO: Implement delete functionality
    console.log("Delete template:", template);
  };

  const handleTemplateUpdate = (updatedTemplate) => {
    // Update the template in the list
    updateTemplateInList(updatedTemplate);
    
    // If this is the currently selected template, update it as well
    if (selectedTemplate && selectedTemplate._id === updatedTemplate._id) {
      setSelectedTemplate(updatedTemplate);
    }
  };

  return (
    <div className="external-templates-page">
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <h1 class="m-0 mb-4">External Templates</h1>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <button className="bouncy-button small" onClick={handleOpenCreateModal}>
              <i className="fas fa-plus"></i> <span>Create Template</span>
            </button>

            <div className="tableWrapper mt-3">
                {loading ? (
                  <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : templates?.length === 0 ? (
                  <div className="text-center">
                    <p>No templates found</p>
                  </div>
                ) : (
                  <TemplateTable
                    templates={templates}
                    onView={handleViewTemplate}
                    onEdit={handleOpenEditModal}
                    onDelete={handleDeleteTemplate}
                  />
                )}

                {!loading && templates?.length > 0 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                )}
            </div>
              
          </div>
        </div>
      </div>

      {/* View Template Modal */}
      <TemplateViewModal
        template={selectedTemplate}
        isOpen={isViewModalOpen}
        onClose={handleCloseModals}
        onTemplateUpdate={handleTemplateUpdate}
      />

      {/* Create Template Modal */}
      <TemplateFormModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseModals}
        onSubmit={handleCreateTemplate}
        title="Create New Template"
        formData={templateForm}
        validationErrors={validationErrors}
        formSubmitting={formSubmitting}
        onFormChange={handleFormChange}
        onVariableChange={handleVariableChange}
        onAddVariable={addVariable}
        onRemoveVariable={removeVariable}
      />

      {/* Edit Template Modal */}
      <TemplateFormModal
        isOpen={isEditModalOpen}
        onClose={handleCloseModals}
        onSubmit={handleUpdateTemplate}
        title="Edit Template"
        formData={templateForm}
        validationErrors={validationErrors}
        formSubmitting={formSubmitting}
        onFormChange={handleFormChange}
        onVariableChange={handleVariableChange}
        onAddVariable={addVariable}
        onRemoveVariable={removeVariable}
      />
    </div>
  );
};

export default ExternalTemplatesPage;
